// stores/useBrokerOAuthStore.js - REFACTORED FOR SECURITY
// Removed persistent localStorage storage - session-only OAuth flow management

import { create } from 'zustand';

/**
 * Cross-browser storage abstraction with fallback mechanisms
 * Handles Safari private browsing, Chrome incognito, and restricted browsers
 */
class SecureStorageManager {
  constructor() {
    this.memoryStorage = new Map();
    this.storageType = this.detectBestStorage();
    console.log(`🔒 SecureStorageManager initialized with: ${this.storageType}`);
  }

  detectBestStorage() {
    // Test storage availability in order of preference
    const storageTests = [
      { name: 'sessionStorage', storage: window.sessionStorage },
      { name: 'localStorage', storage: window.localStorage }
    ];

    for (const { name, storage } of storageTests) {
      try {
        if (!storage) continue;
        
        // Test write/read/remove capability
        const testKey = '__storage_test__';
        const testValue = 'test';
        
        storage.setItem(testKey, testValue);
        const retrieved = storage.getItem(testKey);
        storage.removeItem(testKey);
        
        if (retrieved === testValue) {
          console.log(`✅ ${name} available and working`);
          return name;
        }
      } catch (error) {
        console.warn(`❌ ${name} test failed:`, error.message);
      }
    }
    
    console.warn('⚠️ All storage methods failed, falling back to memory storage');
    return 'memory';
  }

  setItem(key, value) {
    // ALWAYS store in memory first as primary storage
    this.memoryStorage.set(key, value);
    
    // For OAuth state, try to store in both sessionStorage AND localStorage for redundancy
    if (this.isOAuthKey(key)) {
      let storedCount = 0;
      
      // Try sessionStorage first
      try {
        window.sessionStorage.setItem(key, value);
        storedCount++;
        console.log(`💾 Stored '${key}' in sessionStorage`);
      } catch (error) {
        console.warn(`Failed to store '${key}' in sessionStorage:`, error.message);
      }
      
      // Also try localStorage as backup for OAuth data
      try {
        window.localStorage.setItem(key, value);
        storedCount++;
        console.log(`💾 Stored '${key}' in localStorage`);
      } catch (error) {
        console.warn(`Failed to store '${key}' in localStorage:`, error.message);
      }
      
      console.log(`💾 OAuth key '${key}' stored in memory + ${storedCount} persistent storage(s)`);
    } else {
      // For non-OAuth data, use the original logic
      try {
        switch (this.storageType) {
          case 'sessionStorage':
            window.sessionStorage.setItem(key, value);
            break;
          case 'localStorage':
            window.localStorage.setItem(key, value);
            break;
        }
        console.log(`💾 Stored '${key}' in memory + ${this.storageType}`);
      } catch (error) {
        // Silently fail for persistent storage, memory is primary
        console.log(`💾 Stored '${key}' in memory only (${this.storageType} failed)`);
      }
    }
  }

  getItem(key) {
    // ALWAYS check memory first (primary storage)
    let value = this.memoryStorage.get(key) || null;
    
    // If not in memory, try persistent storage
    if (!value) {
      // For OAuth keys, check both sessionStorage and localStorage
      if (this.isOAuthKey(key)) {
        // Try sessionStorage first
        try {
          value = window.sessionStorage.getItem(key);
          if (value) {
            this.memoryStorage.set(key, value);
            console.log(`📖 Retrieved OAuth key '${key}' from sessionStorage and synced to memory`);
          }
        } catch (error) {
          // Silently continue to localStorage
        }
        
        // If not found in sessionStorage, try localStorage
        if (!value) {
          try {
            value = window.localStorage.getItem(key);
            if (value) {
              this.memoryStorage.set(key, value);
              console.log(`📖 Retrieved OAuth key '${key}' from localStorage and synced to memory`);
            }
          } catch (error) {
            // Silently fail
          }
        }
      } else {
        // For non-OAuth keys, use original logic
        try {
          switch (this.storageType) {
            case 'sessionStorage':
              value = window.sessionStorage.getItem(key);
              break;
            case 'localStorage':
              value = window.localStorage.getItem(key);
              break;
          }
          
          // If found in persistent storage, sync to memory
          if (value) {
            this.memoryStorage.set(key, value);
            console.log(`📖 Retrieved '${key}' from ${this.storageType} and synced to memory`);
          }
        } catch (error) {
          // Silently fail, value remains null
        }
      }
    }
    
    console.log(`📖 Retrieved '${key}':`, value ? 'found' : 'not found');
    return value;
  }

  removeItem(key) {
    // Remove from memory first
    this.memoryStorage.delete(key);
    
    // Then attempt to remove from persistent storage
    try {
      switch (this.storageType) {
        case 'sessionStorage':
          window.sessionStorage.removeItem(key);
          break;
        case 'localStorage':
          window.localStorage.removeItem(key);
          break;
      }
      console.log(`🗑️ Removed '${key}' from memory + ${this.storageType}`);
    } catch (error) {
      console.log(`🗑️ Removed '${key}' from memory only`);
    }
  }

  clearOAuthData() {
    try {
      const oauthKeys = [];
      
      // Clear from memory storage
      for (const [key] of this.memoryStorage) {
        if (this.isOAuthKey(key)) {
          oauthKeys.push(key);
        }
      }
      
      // Clear from persistent storage
      if (this.storageType !== 'memory') {
        const storage = this.storageType === 'sessionStorage' 
          ? window.sessionStorage 
          : window.localStorage;
        
        try {
          for (let i = storage.length - 1; i >= 0; i--) {
            const key = storage.key(i);
            if (key && this.isOAuthKey(key)) {
              oauthKeys.push(key);
            }
          }
        } catch (e) {
          // Ignore storage access errors
        }
      }
      
      // Remove all OAuth keys
      [...new Set(oauthKeys)].forEach(key => this.removeItem(key));
      console.log(`🧹 Cleared ${oauthKeys.length} OAuth keys`);
    } catch (error) {
      console.error('❌ Failed to clear OAuth data:', error);
    }
  }

  isOAuthKey(key) {
    return key && (
      key.includes('_oauth_state') || 
      key.includes('_oauth_timestamp') ||
      key.includes('_sandbox_mode') ||
      key.includes('_oauth_redirect_uri')
    );
  }

  getStorageInfo() {
    const memoryKeys = Array.from(this.memoryStorage.keys());
    const sessionKeys = [];
    const localKeys = [];
    
    // Try to get keys from sessionStorage
    try {
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const key = window.sessionStorage.key(i);
        if (key && this.isOAuthKey(key)) {
          sessionKeys.push(key);
        }
      }
    } catch (e) {
      // Ignore
    }
    
    // Try to get keys from localStorage
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && this.isOAuthKey(key)) {
          localKeys.push(key);
        }
      }
    } catch (e) {
      // Ignore
    }
    
    return {
      type: this.storageType,
      memoryKeys,
      sessionKeys,
      localKeys,
      available: {
        sessionStorage: this.isStorageAvailable(window.sessionStorage),
        localStorage: this.isStorageAvailable(window.localStorage),
        memory: true
      }
    };
  }

  isStorageAvailable(storage) {
    try {
      const test = '__test__';
      storage.setItem(test, test);
      storage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
  
  // Force sync all OAuth data from persistent storage to memory
  syncToMemory() {
    if (this.storageType === 'memory') return;
    
    let syncedCount = 0;
    
    // For OAuth keys, check both sessionStorage and localStorage
    // Try sessionStorage first
    try {
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const key = window.sessionStorage.key(i);
        if (key && this.isOAuthKey(key)) {
          const value = window.sessionStorage.getItem(key);
          if (value && !this.memoryStorage.has(key)) {
            this.memoryStorage.set(key, value);
            syncedCount++;
            console.log(`📖 Synced OAuth key '${key}' from sessionStorage to memory`);
          }
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not sync from sessionStorage:', e.message);
    }
    
    // Then try localStorage for any missing OAuth keys
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && this.isOAuthKey(key)) {
          const value = window.localStorage.getItem(key);
          if (value && !this.memoryStorage.has(key)) {
            this.memoryStorage.set(key, value);
            syncedCount++;
            console.log(`📖 Synced OAuth key '${key}' from localStorage to memory`);
          }
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not sync from localStorage:', e.message);
    }
    
    console.log(`✅ Synced ${syncedCount} OAuth data items to memory`);
  }
}

// Create singleton instance
const secureStorage = new SecureStorageManager();

// Sync on creation to handle page refreshes
secureStorage.syncToMemory();

/**
 * Broker OAuth Store - Session-only OAuth flow management
 * 
 * SECURITY IMPROVEMENTS:
 * - Cross-browser storage abstraction with fallback mechanisms
 * - OAuth states stored with sessionStorage preference (cleared on browser close)
 * - Fallback to localStorage and memory storage for restricted browsers
 * - Short-lived state management for OAuth flows
 * - All sensitive tokens handled by SecureBrokerTokenService
 * 
 * USE FOR: Temporary OAuth flow state during authentication
 * DO NOT USE FOR: Token storage, persistent connection state
 */
const useBrokerOAuthStore = create((set, get) => ({
  // Current OAuth flow state (session-only, no persistence)
  currentFlow: null, // { broker, state, timestamp, isSandbox }
  brokerConnecting: null,
  
  // Temporary sandbox settings during OAuth flow (session-only)
  tempSandboxSettings: {}, // { brokerName: boolean }

  // Actions
  startOAuthFlow: (brokerName, state, isSandbox = false) => {
    const flowData = {
      broker: brokerName,
      state: state,
      isSandbox: isSandbox,
      timestamp: Date.now()
    };
    
    set({
      currentFlow: flowData,
      brokerConnecting: brokerName,
      tempSandboxSettings: {
        ...get().tempSandboxSettings,
        [brokerName]: isSandbox
      }
    });
    
    // Store OAuth state using cross-browser storage abstraction
    // Memory is primary, persistent storage is backup
    console.log(`🏪 Storing OAuth state for ${brokerName}: ${state.substring(0, 8)}...`);
    secureStorage.setItem(`${brokerName}_oauth_state`, state);
    secureStorage.setItem(`${brokerName}_oauth_timestamp`, Date.now().toString());
    
    // Verify storage worked
    const verifyState = secureStorage.getItem(`${brokerName}_oauth_state`);
    console.log(`✅ OAuth state stored and verified for ${brokerName}: ${verifyState ? 'SUCCESS' : 'FAILED'}`);
    
    // Store redirect URI for backend validation
    const redirectUri = `${window.location.origin}/broker/callback`;
    secureStorage.setItem(`${brokerName}_oauth_redirect_uri`, redirectUri);
    
    if (isSandbox) {
      secureStorage.setItem(`${brokerName}_sandbox_mode`, 'true');
    }
    
    console.log(`🔄 OAuth flow started for ${brokerName} (sandbox: ${isSandbox})`);
    
    // Debug: verify storage immediately after setting
    const storedStateVerify = secureStorage.getItem(`${brokerName}_oauth_state`);
    console.log(`🔍 Verification - OAuth state stored correctly:`, storedStateVerify === state);
  },

  validateAndClearOAuthFlow: (brokerName, state) => {
    // Sync storage to memory before validation (handles page refresh)
    secureStorage.syncToMemory();
    
    const currentFlow = get().currentFlow;
    const storedState = secureStorage.getItem(`${brokerName}_oauth_state`);
    const storedTimestamp = secureStorage.getItem(`${brokerName}_oauth_timestamp`);
    
    console.log(`🔍 OAuth validation for ${brokerName}:`, {
      providedState: state?.substring(0, 8) + '...',
      storedState: storedState?.substring(0, 8) + '...',
      currentFlowState: currentFlow?.state?.substring(0, 8) + '...',
      statesMatch: storedState === state,
      hasCurrentFlow: !!currentFlow,
      brokerMatches: currentFlow?.broker === brokerName
    });
    
    // Validate state matches (primary check is stored state)
    const isValid = storedState === state && state !== null;
    
    // Check if OAuth flow has expired (10 minutes timeout)
    const OAUTH_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    let isExpired = false;
    if (storedTimestamp) {
      const age = Date.now() - parseInt(storedTimestamp);
      isExpired = age > OAUTH_TIMEOUT;
      if (isExpired) {
        const ageInSeconds = Math.round(age/1000);
        console.warn(`⚠️ OAuth flow expired for ${brokerName} (age: ${ageInSeconds}s)`);
      }
    }
    
    // Only clear OAuth data if validation was successful or if expired
    if (isValid || isExpired) {
      set((state) => ({
        currentFlow: null,
        brokerConnecting: null,
        tempSandboxSettings: {
          ...state.tempSandboxSettings,
          [brokerName]: undefined
        }
      }));
      
      // Clear OAuth data from storage
      secureStorage.removeItem(`${brokerName}_oauth_state`);
      secureStorage.removeItem(`${brokerName}_oauth_timestamp`);
      secureStorage.removeItem(`${brokerName}_sandbox_mode`);
      secureStorage.removeItem(`${brokerName}_oauth_redirect_uri`);
      
      console.log(`🧹 Cleared OAuth data for ${brokerName} (valid: ${isValid}, expired: ${isExpired})`);
    } else {
      console.log(`🔒 Keeping OAuth data for ${brokerName} - validation failed but not expired`);
    }
    
    const finalValid = isValid && !isExpired;
    console.log(`🔍 OAuth validation result for ${brokerName}: ${finalValid ? 'VALID' : 'INVALID'} (valid: ${isValid}, expired: ${isExpired})`);
    return finalValid;
  },

  setBrokerConnecting: (brokerName) => {
    set({ brokerConnecting: brokerName });
    // Also store in sessionStorage for persistence across page reloads
    if (brokerName) {
      secureStorage.setItem('broker_connecting', brokerName);
    }
    console.log(`🔄 Broker connecting: ${brokerName}`);
  },

  clearBrokerConnecting: () => {
    const current = get().brokerConnecting;
    set({ brokerConnecting: null });
    // Also clear from sessionStorage
    secureStorage.removeItem('broker_connecting');
    if (current) {
      console.log(`✅ Cleared broker connecting: ${current}`);
    }
  },

  getSandboxMode: (brokerName) => {
    const tempSetting = get().tempSandboxSettings[brokerName];
    if (tempSetting !== undefined) return tempSetting;
    
    // Check secure storage as fallback
    return secureStorage.getItem(`${brokerName}_sandbox_mode`) === 'true';
  },

  // Clear all OAuth data for a specific broker
  clearBrokerOAuthData: (brokerName) => {
    set((state) => {
      const newTempSettings = { ...state.tempSandboxSettings };
      delete newTempSettings[brokerName];
      
      return {
        currentFlow: state.currentFlow?.broker === brokerName ? null : state.currentFlow,
        brokerConnecting: state.brokerConnecting === brokerName ? null : state.brokerConnecting,
        tempSandboxSettings: newTempSettings
      };
    });
    
    // Clear secure storage for this broker
    secureStorage.removeItem(`${brokerName}_oauth_state`);
    secureStorage.removeItem(`${brokerName}_oauth_timestamp`);
    secureStorage.removeItem(`${brokerName}_sandbox_mode`);
    secureStorage.removeItem(`${brokerName}_oauth_redirect_uri`);
    
    console.log(`🧹 Cleared OAuth data for ${brokerName}`);
  },

  // Clear all OAuth data
  clearAllOAuthData: () => {
    set({
      currentFlow: null,
      brokerConnecting: null,
      tempSandboxSettings: {}
    });
    
    // Clear all OAuth-related data from secure storage
    secureStorage.clearOAuthData();
    
    console.log('🧹 Cleared all OAuth data');
  },

  // Get current OAuth flow info
  getCurrentFlow: () => get().currentFlow,
  
  getBrokerConnecting: () => {
    const storeValue = get().brokerConnecting;
    if (storeValue) return storeValue;
    
    // Fallback to sessionStorage if not in store (e.g., after page reload)
    const storedValue = secureStorage.getItem('broker_connecting');
    if (storedValue) {
      console.log(`📖 Retrieved broker_connecting from storage: ${storedValue}`);
      // Update the store with the retrieved value
      set({ brokerConnecting: storedValue });
      return storedValue;
    }
    
    return null;
  },

  // Check if OAuth flow is active for a broker
  isOAuthFlowActive: (brokerName) => {
    const currentFlow = get().currentFlow;
    return currentFlow?.broker === brokerName;
  },

  // Check if OAuth flow has expired
  isOAuthFlowExpired: (brokerName = null) => {
    const flow = brokerName ? 
      (get().currentFlow?.broker === brokerName ? get().currentFlow : null) :
      get().currentFlow;
      
    if (!flow) return true;
    
    const OAUTH_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    return (Date.now() - flow.timestamp) > OAUTH_TIMEOUT;
  },

  // Get OAuth state from secure storage (for validation)
  getStoredOAuthState: (brokerName) => {
    // Sync before getting to handle page refresh
    secureStorage.syncToMemory();
    return secureStorage.getItem(`${brokerName}_oauth_state`);
  },

  // Get stored redirect URI for validation
  getStoredRedirectUri: (brokerName) => {
    // Sync before getting to handle page refresh
    secureStorage.syncToMemory();
    return secureStorage.getItem(`${brokerName}_oauth_redirect_uri`);
  },

  // Debug: Get all OAuth storage data and storage info
  getOAuthDebugInfo: () => {
    const storageInfo = secureStorage.getStorageInfo();
    
    return {
      storeState: {
        currentFlow: get().currentFlow,
        brokerConnecting: get().brokerConnecting,
        tempSandboxSettings: get().tempSandboxSettings
      },
      storage: storageInfo,
      oauthKeys: {
        memory: storageInfo.memoryKeys.filter(key => secureStorage.isOAuthKey(key)),
        session: storageInfo.sessionKeys,
        local: storageInfo.localKeys
      }
    };
  },

  // Get storage manager instance for advanced usage
  getStorageManager: () => secureStorage,
  
  // Force sync storage to memory (useful after page refresh)
  syncStorage: () => {
    secureStorage.syncToMemory();
    console.log('✅ OAuth storage synced to memory');
  }
}));

export default useBrokerOAuthStore;
